import React, { memo } from 'react';
import styled, { css } from 'styled-components';
import { HiOutlineQrcode, HiOutlineCursorClick } from 'react-icons/hi';
import { SCAN_RESULT_MESSAGE, SCAN_RESULT_NOTE, SCAN_READY_MESSAGE, SCAN_PAUSED_MESSAGE, isCheckable } from '../utils/utils';
import ParticipantDetailCard from './ParticipantDetailCard';
import ScanResultBanner from './ScanResultBanner';
import ScanCheckInButtons from './ScanCheckInButtons';
import RecentProcessList from './RecentProcessList';
import type { ScanResult, ScanParticipant, RecentProcessItem } from '../types/types';

type QrScanPanelProps = {
	scanResult: ScanResult | null;
	recentProcesses: RecentProcessItem[];
	isPaused: boolean; // 창 포커스 아웃으로 스캔 일시 정지 여부
	onCompleteCheckIn: (participant: ScanParticipant) => void;
	onClearResult: () => void;
	onResume: () => void;
};

// QR 스캔 모드 패널
const QrScanPanel = memo<QrScanPanelProps>(({ scanResult, recentProcesses, isPaused, onCompleteCheckIn, onClearResult, onResume }) => {
	return (
		<Wrapper>
			{/* 스캔 대기 상태 안내 (준비 완료 / 일시 정지) */}
			{!scanResult && (
				<ScanResultBanner
					variant={isPaused ? 'error' : 'successPill'}
					text={isPaused ? SCAN_PAUSED_MESSAGE : SCAN_READY_MESSAGE}
				/>
			)}

			{/* 포커스 아웃 → 스캔 일시 정지 안내 (클릭 시 재활성화) */}
			{!scanResult && isPaused && (
				<IdleBox $paused={true} onClick={onResume}>
					<HiOutlineCursorClick fontSize={44} />
					<IdleTitle $paused={true}>스캔 모드가 종료되었습니다.</IdleTitle>
					<IdleDescription $paused={true}>이 화면 아무곳이나 클릭하시면 다시 스캔 모드가 활성화됩니다.</IdleDescription>
				</IdleBox>
			)}

			{/* 스캔 결과 없음 → 대기 안내 */}
			{!scanResult && !isPaused && (
				<IdleBox>
					<HiOutlineQrcode fontSize={44} />
					<IdleTitle>QR 스캔모드입니다</IdleTitle>
					<IdleDescription>리더기에 QR을 향하면 자동으로 처리됩니다</IdleDescription>
				</IdleBox>
			)}

			{/* 체크인 가능 → 확인 카드 + 체크인 완료/취소 */}
			{scanResult && isCheckable(scanResult.status) && scanResult.participant && (
				<ResultBox>
					<ScanResultBanner variant="successPill" text={SCAN_RESULT_MESSAGE.CHECKABLE} />
					<ParticipantDetailCard
						participant={scanResult.participant}
						tone="blue"
						footer={
							<ScanCheckInButtons
								onComplete={() => onCompleteCheckIn(scanResult.participant as ScanParticipant)}
								onCancel={onClearResult}
							/>
						}
					/>
				</ResultBox>
			)}

			{/* 취소/이미수령 → 참가자 정보 + 확인 */}
			{scanResult && !isCheckable(scanResult.status) && scanResult.participant && (
				<ResultBox>
					<ScanResultBanner variant="error" text={SCAN_RESULT_MESSAGE[scanResult.status]} />
					<ParticipantDetailCard
						participant={scanResult.participant}
						tone="orange"
						footer={<ConfirmButton onClick={onClearResult}>확인</ConfirmButton>}
					/>
				</ResultBox>
			)}

			{/* 시간 외/훼손 → 안내 문구 카드 + 확인 */}
			{scanResult && !scanResult.participant && (
				<ResultBox>
					<ScanResultBanner variant="error" text={SCAN_RESULT_MESSAGE[scanResult.status]} />
					<NoteCard>
						<NoteText>{SCAN_RESULT_NOTE[scanResult.status]}</NoteText>
						<ConfirmButton onClick={onClearResult}>확인</ConfirmButton>
					</NoteCard>
				</ResultBox>
			)}

			<RecentProcessList items={recentProcesses} showTime={!!scanResult} />
		</Wrapper>
	);
});

QrScanPanel.displayName = 'QrScanPanel';

export default QrScanPanel;

const Wrapper = styled.div`
	padding-top: 24px;
`;
const IdleBox = styled.div<{ $paused?: boolean }>`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 16px;
	padding: 120px 0;
	margin-top: 20px;
	border-radius: 8px;
	border: 1px solid ${({ theme }) => theme.colors.activeBlue};
	color: ${({ theme }) => theme.colors.deepGrayFontColor};

	${({ $paused, theme }) =>
		$paused &&
		css`
			cursor: pointer;
			border-color: ${theme.colors.activeRedOrange};
			background-color: #ffede3;
			color: ${theme.colors.activeRedOrange};
		`};
`;
const IdleTitle = styled.div<{ $paused?: boolean }>`
	font-size: 16px;
	font-family: PretendardSemiBold, AppleSDGothicNeoSemiBold, sans-serif;
	color: ${({ $paused, theme }) => ($paused ? theme.colors.activeRedOrange : theme.colors.blackColor)};
`;
const IdleDescription = styled.div<{ $paused?: boolean }>`
	font-size: 14px;
	text-align: center;
	color: ${({ $paused, theme }) => ($paused ? theme.colors.activeRedOrange : theme.colors.deepGrayFontColor)};
`;
const ResultBox = styled.div`
	display: flex;
	flex-direction: column;
	gap: 20px;
`;
const NoteCard = styled.div`
	min-height: 360px;
	display: flex;
	flex-direction: column;
	padding: 32px;
	border-radius: 8px;
	background-color: #ffede3;
`;
const NoteText = styled.div`
	font-size: 16px;
	font-family: PretendardSemiBold, AppleSDGothicNeoSemiBold, sans-serif;
	color: ${({ theme }) => theme.colors.activeRedOrange};
	padding-bottom: 24px;
	border-bottom: 1px solid rgba(0, 0, 0, 0.08);
`;
const ConfirmButton = styled.button`
	width: 100%;
	height: 56px;
	font-size: 16px;
	font-family: PretendardSemiBold, AppleSDGothicNeoSemiBold, sans-serif;
	margin-top: auto;
	border-radius: 6px;
	cursor: pointer;
	color: ${({ theme }) => theme.colors.deepGrayFontColor};
	background-color: ${({ theme }) => theme.colors.whiteColor};
	transition: 0.2s;

	&:hover {
		background-color: ${({ theme }) => theme.colors.lightGrayHoverColor};
	}
`;
